/*
 * Copyright © 2018-2025, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { useEffect, useRef, useState } from "react";
import { SetupIntent } from "@stripe/stripe-js";
import { strings } from "../../../common/Strings/Strings";
import AlertBox, { AlertType } from "../../../components/AlertBox";
import { SetupIntentResponse } from "../../../models/stripe/SetupIntentResponse";
import StripeApi from "../../../api/StripeApi";
import { getGeneralError } from "../../../util/helperFunctions";
import StripeSection from "../../Stripe/StripeSection";
import StripeSetupForm, {
  StripeSetupFormRef,
} from "../../Stripe/StripeSetupForm";
import LoaderInline from "../../../components/LoaderInline";
import Button from "../../../components/Button";
import { insertReactElements } from "../../../util/HtmlUtils";
import SimpleCheckBox from "../../../components/InputFieldsSimple/SimpleCheckBox";
import { PendingClinicRegistrationResponse } from "../../../models/clinic/PendingClinicRegistrationResponse";
import ClinicRegistrationApi from "../../../api/ClinicRegistrationApi";
import { CreditCardRequest } from "../../../models/clinic/CreditCardRequest";

interface Props {
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  registration?: PendingClinicRegistrationResponse;
  onNext(registration: PendingClinicRegistrationResponse): void;
}

const CreditCardTab: React.FC<Props> = (props: Props) => {
  const { registration, onNext, loading, setLoading } = props;

  const [isStripeFormValid, setStripeFormValid] = useState<boolean>(false);
  const stripeSetupRef = useRef<StripeSetupFormRef>(null);
  const [stripeSetupIntent, setStripeSetupIntent] =
    useState<SetupIntentResponse>();
  const [confirmedSetupIntent, setConfirmedSetupIntent] =
    useState<SetupIntent>();
  const [serverError, setServerError] = useState<null | string>(null);

  useEffect(() => {
    if (!registration?.countryCode) {
      return;
    }
    (async () => {
      setLoading(true);

      try {
        const response = await StripeApi.getSetupIntent(
          registration.countryCode
        );
        setStripeSetupIntent(response.data);
      } catch (err) {
        setServerError(await getGeneralError(err));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registration]);

  const submit = async (data: SetupIntent) => {
    if (!registration) return;
    setLoading(true);
    setServerError(null);

    try {
      const request: CreditCardRequest = { stripeSetupIntentId: data.id };
      const response = await ClinicRegistrationApi.addCreditCard(
        registration.id,
        request
      );
      onNext(response.data);
    } catch (e) {
      setServerError(await getGeneralError(e));
    } finally {
      setLoading(false);
    }
  };

  const confirmStripeSetup = async () => {
    if (confirmedSetupIntent) {
      await submit(confirmedSetupIntent);
      return;
    }

    if (!stripeSetupRef.current) return;
    setLoading(true);

    try {
      const response = await stripeSetupRef.current.confirmSetup();
      if (response?.setupIntent && !response?.error) {
        setConfirmedSetupIntent(response.setupIntent);
        await submit(response.setupIntent);
      } else {
        setServerError(response?.error?.message ?? strings.simpleError);
      }
    } catch (e) {
      const err: any = e;
      setServerError(err?.message ?? strings.simpleError);
    } finally {
      setLoading(false);
    }
  };

  const [policiesAccepted, setPoliciesAccepted] = useState<boolean>(false);

  const handleCheckBox = (checked: boolean) => {
    setPoliciesAccepted(checked);
  };

  const generalTermsLink = (
    <Button
      className="text-sm"
      onClick={() =>
        window.open(
          registration?.countryCode
            ? `/general-terms-and-conditions/${registration.countryCode}`
            : "/general-terms-and-conditions",
          "_blank",
          "noreferrer"
        )
      }
      variant="link"
    >
      {strings.clinicCreationAgreementGeneral}
    </Button>
  );

  const dataProcessingTermsLink = (
    <Button
      className="text-sm"
      onClick={() =>
        window.open(
          registration?.countryCode
            ? `/data-processing-terms-and-conditions/${registration.countryCode}`
            : "/data-processing-terms-and-conditions",
          "_blank",
          "noreferrer"
        )
      }
      variant="link"
    >
      {strings.clinicCreationAgreementDataProcessing}
    </Button>
  );

  return (
    <>
      <LoaderInline hidden={!loading} />
      <div hidden={loading} className="space-y-6">
        <AlertBox
          closeAble={false}
          type={AlertType.INFO}
          message={strings.chargeWarning}
        />
        <div>
          {stripeSetupIntent && (
            <StripeSection
              apiKey={stripeSetupIntent.publicKey}
              clientSecret={stripeSetupIntent.clientSecret}
            >
              <StripeSetupForm
                ref={stripeSetupRef}
                onValidation={(valid) => setStripeFormValid(valid)}
                address={registration?.addresses[0]}
              />
            </StripeSection>
          )}
        </div>
        <div className="flex items-center text-sm">
          <SimpleCheckBox name="acceptPolicies" onChange={handleCheckBox} />
          {insertReactElements(strings.clinicCreationAgreement, [
            generalTermsLink,
            dataProcessingTermsLink,
          ])}
        </div>
        <AlertBox message={serverError} />
        <Button
          disabled={
            loading ||
            (!isStripeFormValid && confirmedSetupIntent === undefined) ||
            !policiesAccepted
          }
          loading={loading}
          onClick={() => confirmStripeSetup()}
          type="button"
          variant="primary"
        >
          {strings.saveAndCreate}
        </Button>
      </div>
    </>
  );
};

export default CreditCardTab;
